'use client'

import Logo from '@/assets/logo.svg'
import { formatImageURL } from '@/utils/util'
import Image from 'next/image'
import { useState } from 'react'
import { AlertTriangle, Mail, Send } from 'lucide-react'
import { Button } from '../elements/Button'
import Input from '../elements/Input'
import Textarea from '../elements/Textarea'
import { useVendorReport } from '@/lib/commerce/hooks/useVendorReport'
import { REPORT_REASONS } from '@/utils/report'

const SUPPORT_EMAIL = 'support@shop.mn'

// Shown when Vendor.status === 'frozen' — shop.mn has paused the shop while it reviews
// complaints against it. Deliberately NOT the Inactive page: that one is a marketing page
// with "try shop.mn free" CTAs, which is the wrong thing to show someone who just lost money
// to this shop. No refund is promised anywhere here — order payments settle straight into the
// vendor's own merchant account, so shop.mn holds nothing it could give back.
export default function Frozen({ vendor }: { vendor: any }) {
  const [submitted, setSubmitted] = useState(false)
  const report = useVendorReport(vendor?.id, () => setSubmitted(true))

  const publicText = vendor?.freeze?.publicText || 'Энэ дэлгүүрийн үйл ажиллагааг Shop.mn түр хугацаанд шалгаж байна.'

  return (
    <div className="relative min-h-screen bg-white">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 max-w-2xl w-full h-36 flex items-start justify-center pt-12">
        <div aria-hidden="true" className="absolute inset-0">
          <div className="absolute top-[35%] left-[30%] -translate-x-1/2 -translate-y-1/2 w-[60%] h-[80%] rounded-full bg-[#4F6AF1] opacity-30 blur-[60px]" />
          <div className="absolute top-1/2 left-[58%] -translate-x-1/2 -translate-y-1/2 w-[80%] h-[120%] rounded-full opacity-15 blur-[60px]" style={{ background: 'linear-gradient(180deg, #FF57EB 0%, #F8D000 100%)' }} />
        </div>
        <Image src={Logo} alt="Shop.mn" height={48} className="relative h-8 md:h-10 xl:h-12 w-auto" />
      </div>

      <div className="relative mx-auto max-w-2xl px-4 pt-44 pb-20">
        <div className="flex flex-col items-center">
          <div className="rounded-[40px] p-1 mb-8 size-28 md:size-32 flex items-center justify-center bg-zinc-200 border border-zinc-300">
            <div className="w-full h-full rounded-[36px] border border-zinc-300 bg-white flex items-center justify-center px-2">
              {vendor?.logo ? (
                <img src={formatImageURL(vendor?.logo)} alt={vendor?.name} className="max-h-16 md:max-h-20 max-w-20 md:max-w-24 w-auto shrink-0" />
              ) : (
                <div className="text-xl md:text-2xl font-bold tracking-tight text-center">{vendor?.name}</div>
              )}
            </div>
          </div>

          <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 mb-4">
            <AlertTriangle className="size-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">Түр хязгаарлагдсан</span>
          </div>

          <h1 className="text-2xl md:text-3xl font-gilroy font-semibold tracking-tight text-center mb-3">
            Энэ дэлгүүр түр хязгаарлагдсан байна
          </h1>
          <p className="text-muted-foreground font-gilroy text-base md:text-lg text-center leading-7 max-w-xl mb-8">
            {publicText}
          </p>
        </div>

        <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-5 md:p-6 mb-6">
          <p className="text-sm font-semibold mb-1">Shop.mn тусламжийн төв</p>
          <p className="text-sm text-muted-foreground mb-4">
            Энэ дэлгүүрт захиалга өгсөн эсвэл төлбөр төлсөн бол доорх маягтыг бөглөж гомдлоо илгээнэ үү.
            Имэйлээр ч бас хандаж болно. Гомдлыг Даваа - Баасан гарагт 10:00 - 17:00 цагт шалгаж хариу өгнө.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button tag="a" href={`mailto:${SUPPORT_EMAIL}`} variant="outline" icon={Mail} className="sm:w-auto">
              {SUPPORT_EMAIL}
            </Button>
          </div>
        </div>

        {submitted ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center">
            <p className="text-base font-semibold text-emerald-800 mb-1">Гомдол хүлээн авлаа</p>
            <p className="text-sm text-emerald-700">
              Shop.mn таны гомдлыг шалгаж, таны үлдээсэн имэйл хаягаар холбогдоно.
            </p>
            <Button variant="ghost" onClick={() => setSubmitted(false)} className="mt-4">
              Дахин гомдол илгээх
            </Button>
          </div>
        ) : (
          <div className="rounded-2xl border border-zinc-200 p-5 md:p-6">
            <p className="text-base font-semibold mb-1">Гомдол илгээх</p>
            <p className="text-sm text-muted-foreground mb-5">
              Захиалгын дугаар болон юу болсныг бичиж үлдээгээрэй. Shop.mn шууд хүлээн авна.
            </p>

            <div className="mb-4">
              <p className="text-sm font-medium mb-2">Шалтгаан</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {REPORT_REASONS.map(({ value, label, sub, Icon }) => {
                  const selected = report.reason === value
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => report.setReason(value)}
                      className={`flex items-start gap-2.5 rounded-xl border p-3 text-left transition-colors ${selected ? 'border-foreground bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'}`}
                    >
                      <Icon className={`size-4 mt-0.5 shrink-0 ${selected ? 'text-foreground' : 'text-muted-foreground'}`} />
                      <span>
                        <span className="block text-sm font-medium leading-5">{label}</span>
                        <span className="block text-xs text-muted-foreground leading-4 mt-0.5">{sub}</span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <Input label="Нэр" value={report.guestName} onChange={(e) => report.setGuestName(e.target.value)} placeholder="Таны нэр" />
              <Input label="Утас" value={report.guestPhone} onChange={(e) => report.setGuestPhone(e.target.value)} placeholder="99112233" inputMode="numeric" />
              <Input label="Имэйл" type="email" value={report.guestEmail} onChange={(e) => report.setGuestEmail(e.target.value)} placeholder="mail@example.com" />
            </div>

            <Textarea
              label="Юу болсон бэ?"
              value={report.message}
              onChange={(e: any) => report.setMessage(e.target.value)}
              placeholder="Захиалгын дугаар, төлсөн дүн, огноо болон юу болсныг бичнэ үү."
              container="mb-5"
            />

            <Button onClick={report.submit} isLoading={report.submitting} icon={Send} width="full">
              Гомдол илгээх
            </Button>
          </div>
        )}

        <p className="text-xs text-muted-foreground text-center mt-6 leading-5">
          Shop.mn нь худалдан авалтын төлбөрийг өөртөө хадгалдаггүй тул төлбөрийг шууд буцаах боломжгүй.
          Бид дэлгүүртэй холбогдож асуудлыг шийдвэрлэхэд туслана.
        </p>
      </div>
    </div>
  )
}
